<template>
    <v-container>
        <v-row>
            <v-col>
                <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
                <v-btn :to="backRoute" prepend-icon="mdi-arrow-left-circle" variant="tonal">Tilbake til listen</v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col>
                <v-card>
                    <v-toolbar>
                        <v-btn
                            v-if="store.hasDeleteNonWhitelistedProfilesLink"
                            density="comfortable"
                            prepend-icon="mdi-delete"
                            @click="confirmDialog = true"
                        >
                            Slett alle ikke-hviteliste profiler
                        </v-btn>
                        <v-dialog v-model="confirmDialog" max-width="290" persistent>
                            <v-card>
                                <v-card-text>Er du sikker på at du vil slette alle disse profilene?</v-card-text>
                                <v-card-actions>
                                    <v-spacer></v-spacer>
                                    <v-btn color="green darken-1" flat @click="confirmDialog = false">Avbryt</v-btn>
                                    <v-btn color="green darken-1" flat @click="deleteNonWhitelistedProfiles">Ok</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-dialog>
                    </v-toolbar>
                    <v-data-table-server
                        :headers="headers"
                        :items="store.nonWhitelistedProfiles"
                        :items-length="paging.totalItemCount"
                        :items-per-page="paging.pageSize"
                        :items-per-page-options="DefaultPerPageOptions"
                        :loading="isLoading"
                        :page="paging.pageNumber"
                        @update:items-per-page="onItemsPerPageChange"
                        @update:page="onPageChange"
                    >
                        <template #item.phone="{ item }">
                            {{ formatters.toFullPhone(item.phone) }}
                        </template>
                        <template #item.controls="{ item }">
                            <router-link
                                :to="{ name: ProfileRels.UpdateProfile, params: { id: item.id } }"
                                class="card-link"
                                target="_blank"
                            >
                                <v-icon class="edit" icon="mdi-account-edit" />
                            </router-link>
                        </template>
                    </v-data-table-server>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useProfileWhitelistStore } from './profileWhitelistStore';
import { useFormatters } from '@/features/shared/formatters';
import { ProfileWhitelistRels } from './rels';
import { DefaultPerPageOptions, PhoneCountryCode } from '@/features/shared/models';
import { useCommonStore } from '@/features/shared/commonStore';
import { ProfileRels } from '@/features/profiles/rels';
import { VDataTableServer } from 'vuetify/components';

const store = useProfileWhitelistStore();
const commonStore = useCommonStore();
const formatters = useFormatters();
const route = useRoute();

const breadcrumbs = ref<Array<any>>([
    {
        title: 'Ikke-hviteliste profiler',
        disabled: false,
        to: {
            name: ProfileWhitelistRels.GetNonWhitelistedProfileStats
        }
    }
]);

const headers = [
    {
        title: 'Fornavn',
        key: 'firstName',
        sortable: true
    },
    {
        title: 'Etternavn',
        key: 'lastName',
        sortable: true
    },
    {
        title: 'Telefon',
        key: 'phone',
        sortable: true
    },
    {
        title: '',
        key: 'controls',
        sortable: false
    }
];

const isLoading = ref(true);
const backRoute = ref({ name: ProfileWhitelistRels.GetNonWhitelistedProfileStats });
const phoneCountryCodes = ref<Array<PhoneCountryCode>>([]);
const confirmDialog = ref(false);
const paging = computed(() => store.nonWhitelistedProfilesPaging);

onMounted(async () => {
    phoneCountryCodes.value = await commonStore.getCountryPhoneCodes();
    store.nonWhitelistedQuery.phoneCountryCode = route.params.phoneCountryCode as string;
    breadcrumbs.value.push({ title: mapCountryCodeToName(store.nonWhitelistedQuery.phoneCountryCode) });
    await store.loadNonWhitelistedProfiles();

    isLoading.value = false;
});

function mapCountryCodeToName(phoneCountryCode?: string): string {
    if (!phoneCountryCode) return '';
    const country = phoneCountryCodes.value.find((code: PhoneCountryCode) => code.code === phoneCountryCode);
    return country ? country.name : phoneCountryCode;
}

async function deleteNonWhitelistedProfiles() {
    confirmDialog.value = false;
    await store.deleteNonWhitelistedProfiles();
    await store.loadNonWhitelistedProfiles();
}

async function onPageChange(pageNumber: number) {
    store.nonWhitelistedProfilesPaging.pageNumber = pageNumber;
    await store.loadNonWhitelistedProfiles();
}

async function onItemsPerPageChange(pageSize: number) {
    store.nonWhitelistedProfilesPaging.pageSize = pageSize;
    store.nonWhitelistedProfilesPaging.pageNumber = 1; // Reset to first page
    await store.loadNonWhitelistedProfiles();
}
</script>

<style lang="scss" scoped>
.card-link {
    color: #000000;
}
</style>
